// Plain-language status lines for the guardian app ("Maya left with Joe at 3:02 PM").
import { time, day } from "../shared/format.ts";

export interface GuardianChild { id: string; first: string; last: string; room_id: string | null; photo_sha256: string | null; relation: string }
export interface GuardianEvent {
  seq: number; id: string; type: string; child_id: string | null; server_ts: number; device_ts: number;
  adult: { id: string | null; first: string; last: string | null } | null;
  photo_sha256: string | null; stroke_sha256: string | null; event_hash: string;
  /** Transfers: where the child was sent ("door", "after_school", "room"). */
  to?: string | null;
  /** A correction that withdrew an absence marked by mistake: the absence's seq. */
  corrects?: number | null;
  /** A drop-off with no adult: the child came in on their own. */
  walk_in?: boolean;
  /** A pickup with no adult: home on their own, or with this sibling (first name). */
  home_alone?: boolean;
  sibling?: string | null;
  reunification?: boolean;
  /** A drop-off after the school's late time. */
  late?: boolean;
}
export interface GuardianSite { site: { id: string; name: string; tz: string; reunification?: { since: number; place: string | null } | null; app_requests?: { pickup: boolean; dropoff: boolean } }; adult_id: string; children: GuardianChild[]; recent: GuardianEvent[]; modules?: string[]; waiting?: { child_id: string; kind: string; status: string }[] }
export interface Overview { sites: GuardianSite[] }

/**
 * Why this child cannot be picked up or dropped off now, or null: already picked up or already at
 * school today, or a request of that kind still waiting. The server refuses the same (409 already_done).
 */
export function handoffBlock(s: GuardianSite, child: GuardianChild, kind: "pickup" | "dropoff", now = Date.now()): string | null {
  if ((s.waiting ?? []).some((w) => w.child_id === child.id && w.kind === kind)) return kind === "pickup" ? "Pickup already asked for" : "Drop-off already sent";
  const where = whereToday(child, s, now);
  if (kind === "pickup" && where === "gone") return "Already picked up today";
  if (kind === "dropoff" && where === "here") return "Already at school";
  return null;
}

/**
 * Where today's records leave the child, as the server's presence does: dropped off or moved
 * (class, after-school) is "here", picked up is "gone", nothing yet (or absent) is "none".
 */
export function whereToday(child: GuardianChild, s: GuardianSite, now = Date.now()): "here" | "gone" | "none" {
  const e = todayStatus(child, s.recent, s.site.tz, now).event;
  if (e?.type === "dropoff" || e?.type === "transfer") return "here";
  if (e?.type === "pickup") return "gone";
  return "none";
}

/** Whether the school lets parents ask for this from the app without a door code (older cached overviews: drop-off only). */
export const allowsAppRequest = (s: GuardianSite, kind: "pickup" | "dropoff") => (s.site.app_requests ?? { pickup: false, dropoff: true })[kind];

export function adultName(e: GuardianEvent): string | null {
  return e.adult ? [e.adult.first, e.adult.last].filter(Boolean).join(" ") : null;
}

/** One sentence for an event about a child. */
export function sentence(e: GuardianEvent, childFirst: string, tz: string): string {
  const at = time(e.server_ts, tz);
  const who = adultName(e);
  switch (e.type) {
    case "pickup": return e.reunification ? `${childFirst} was released to ${who ?? "you"} at the reunification point at ${at}` : e.home_alone ? `${childFirst} went home on their own at ${at}` : e.sibling ? `${childFirst} left with ${e.sibling} at ${at}` : who ? `${childFirst} left with ${who} at ${at}` : `${childFirst} left at ${at}`;
    case "dropoff": return e.late ? `${childFirst} arrived late at ${at}` : e.walk_in ? `${childFirst} came in on their own at ${at}` : who ? `${childFirst} was dropped off by ${who} at ${at}` : `${childFirst} was dropped off at ${at}`;
    case "absence": return `${childFirst} was marked absent at ${at}`;
    case "transfer": return `${childFirst} moved at ${at}`;
    case "correction": return e.corrects ? `${childFirst}'s absence was withdrawn at ${at}` : `The office corrected a record at ${at}`;
    default: return `${childFirst}: update at ${at}`;
  }
}

/** Local calendar day key (YYYY-MM-DD) in the site's time zone. */
export const dayKey = (ts: number, tz: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(ts));

export type TodayTone = "neutral" | "success" | "info" | "warning";

/** Today's status from the child's most recent event today (events are newest first). */
export function todayStatus(child: GuardianChild, events: GuardianEvent[], tz: string, now = Date.now()): { label: string; tone: TodayTone; line: string; event: GuardianEvent | null } {
  const today = dayKey(now, tz);
  const e = events.find((x) => x.child_id === child.id && dayKey(x.server_ts, tz) === today) ?? null;
  if (!e) return { label: "No updates yet", tone: "neutral", line: "Nothing recorded today yet.", event: null };
  const line = sentence(e, child.first, tz);
  switch (e.type) {
    case "pickup": return { label: "Picked up", tone: "success", line, event: e };
    case "dropoff": return { label: "At school", tone: "info", line, event: e };
    case "absence": return { label: "Absent", tone: "warning", line, event: e };
    case "correction": if (e.corrects) return { label: "Not absent", tone: "neutral", line, event: e }; return { label: "Updated", tone: "neutral", line, event: e };
    default: return { label: "Updated", tone: "neutral", line, event: e };
  }
}

/** Groups events by local day, newest day first, with a heading like "Tue, Sep 22". */
export function byDay(events: GuardianEvent[], tz: string): { key: string; heading: string; events: GuardianEvent[] }[] {
  const groups = new Map<string, GuardianEvent[]>();
  for (const e of events) {
    const k = dayKey(e.server_ts, tz);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(e);
  }
  return [...groups.entries()].map(([key, evs]) => ({ key, heading: day(evs[0]!.server_ts, tz), events: evs }));
}

/** The four steps of a child's day on the parent's home screen (redesign 2026-09-27). */
export interface DayTrack {
  title: string;
  since: string | null;
  absent: boolean;
  /** Dropped off, In class, Called, Picked up: done, now, or not yet. */
  steps: { label: string; state: "done" | "now" | "next"; at: string | null }[];
}

export function dayTrack(child: GuardianChild, events: GuardianEvent[], tz: string, now = Date.now()): DayTrack {
  const today = dayKey(now, tz);
  const mine = events.filter((e) => e.child_id === child.id && dayKey(e.server_ts, tz) === today).sort((a, b) => a.server_ts - b.server_ts);
  const drop = mine.find((e) => e.type === "dropoff") ?? null;
  const pick = [...mine].reverse().find((e) => e.type === "pickup") ?? null;
  // An absence withdrawn later the same day (marked by mistake) no longer counts.
  const withdrawn = new Set(mine.filter((e) => e.type === "correction" && e.corrects).map((e) => e.corrects));
  const absence = mine.find((e) => e.type === "absence" && !withdrawn.has(e.seq)) ?? null;
  const called = [...mine].reverse().find((e) => e.type === "transfer" && (e.to === "door" || e.to === "after_school")) ?? null;
  const at = (e: GuardianEvent | null) => (e ? time(e.server_ts, tz) : null);
  const labels = ["Dropped off", "In class", called?.to === "after_school" ? "After-school" : "Called", "Picked up"];
  if (absence && !drop) {
    return { title: `${child.first} is absent today`, since: at(absence), absent: true, steps: labels.map((label) => ({ label, state: "next" as const, at: null })) };
  }
  // How far the day has got: 0 nothing yet, 1 dropped off, 2 in class, 3 called, 4 picked up.
  const reached = pick ? 4 : called ? 3 : drop ? 2 : 0;
  const steps = labels.map((label, i) => ({
    label,
    state: (i + 1 < reached || reached === 4 ? "done" : i + 1 === reached ? "now" : "next") as "done" | "now" | "next",
    at: i === 0 ? at(drop) : i === 2 ? at(called) : i === 3 ? at(pick) : null,
  }));
  if (reached >= 2) steps[0]!.state = "done";
  const who = pick ? adultName(pick) : null;
  const title = pick ? `${child.first} is on the way home` : called ? (called.to === "after_school" ? `${child.first} is at after-school` : `${child.first} is at the door`) : drop ? `${child.first} is in class` : `${child.first} isn't there yet`;
  const since = pick ? `${who ? `With ${who} · ` : ""}since ${at(pick)}` : called ? `Since ${at(called)}` : drop ? `Since ${at(drop)}` : null;
  return { title, since, absent: false, steps };
}
